
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || "/.netlify/functions/api";

const AddLecturerModal = ({ isOpen, onClose, onSubmit }) => {
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    lecturerName: '',
    email: '',
    specialization: '',
    collegeName: '',
    contactInfo: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e2) => {
        setImagePreview(e2.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({
      lecturerName: '',
      email: '',
      specialization: '',
      collegeName: '',
      contactInfo: ''
    });
    setSelectedImage(null);
    setImagePreview(null);
    setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const { lecturerName, email, specialization, collegeName, contactInfo } = formData;


    if (!user || !user.email) {
      setErrorMsg('لازم تسجل دخول عشان تقدر ترسل طلب إضافة محاضر.');
      return;
    }

    if (
      !lecturerName.trim() ||
      !email.trim() ||
      !specialization.trim() ||
      !collegeName.trim()
    ) {
      setErrorMsg('رجاءً عبّ كل الحقول المطلوبة.');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');

    try {
      const res = await fetch(`${API_BASE}/lecturer-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          lecturer_name: lecturerName.trim(),
          email: email.trim(),
          specialization: specialization.trim(),
          college_name: collegeName.trim(),
          contact_info: contactInfo.trim() || null,
          image_data: imagePreview || null,
          requester_email: user.email
        })
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'failed');
      }

      const data = await res.json();

      if (onSubmit) {
        onSubmit(data);
      }

      resetForm();
      onClose();
    } catch (err) {
      console.error(err);
      setErrorMsg('ما قدرنا نسجل طلب المحاضر، جرّب مرة ثانية.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div
        className="rounded-xl p-6 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto scrollbar-hide"
        style={{
          background:
            'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), rgb(8, 20, 35)',
          borderRadius: '12px',
          boxShadow: 'rgba(255, 255, 255, 0.08) 0px 8px 32px 0px',
          backdropFilter: 'blur(2px)'
        }}
      >
        <button
          onClick={() => {
            if (!submitting) {
              resetForm();
              onClose();
            }
          }}
          className="absolute top-4 left-4 w-8 h-8 bg-[#1a2a3a] rounded-lg flex items-center justify-center hover:bg-[#2a3a4a] transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">اطلب إضافة محاضر</h2>
          <p className="text-gray-300 text-sm">
            عبّ بيانات المحاضر اللي تبغى إضافته وسيتم اضافته خلال 24.
          </p>
        </div>

        <div className="bg-[#1a2a3a] rounded-xl p-8 mb-6 text-center">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
            id="lecturer-image-upload"
          />
          <label
            htmlFor="lecturer-image-upload"
            className="cursor-pointer flex flex-col items-center gap-3"
          >
            <div className="w-16 h-16 bg-[#2a3a4a] rounded-full flex items-center justify-center hover:bg-[#3a4a5a] transition-colors">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <svg
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.5 10.5C9.95963 10.5 10.4148 10.4095 10.8394 10.2336C11.264 10.0577 11.6499 9.79988 11.9749 9.47487C12.2999 9.14987 12.5577 8.76403 12.7336 8.33939C12.9095 7.91475 13 7.45963 13 7C13 6.54037 12.9095 6.08525 12.7336 5.66061C12.5577 5.23597 12.2999 4.85013 11.9749 4.52513C11.6499 4.20012 11.264 3.94231 10.8394 3.76642C10.4148 3.59053 9.95963 3.5 9.5 3.5C8.57174 3.5 7.6815 3.86875 7.02513 4.52513C6.36875 5.1815 6 6.07174 6 7C6 7.92826 6.36875 8.8185 7.02513 9.47487C7.6815 10.1313 8.57174 10.5 9.5 10.5Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M18.5 15.5V20.5M18.5 15.5L20.5 17.5M18.5 15.5L16.5 17.5M13.5 14.5H9.4C7.16 14.5 6.04 14.5 5.184 14.936C4.43139 15.3195 3.81949 15.9314 3.436 16.684C3 17.54 3 18.66 3 20.9V21.5H13.5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <p className="text-gray-300 text-sm">
              {imagePreview ? 'تغيير صورة المحاضر' : 'أضف صورة للمحاضر (اختياري)'}
            </p>
          </label>
        </div>

        {errorMsg && (
          <div className="mb-4 text-right text-sm text-red-400">
            {errorMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-white text-sm font-medium mb-2 text-right">
              اسم المحاضر
            </label>
            <input
              type="text"
              name="lecturerName"
              value={formData.lecturerName}
              onChange={handleInputChange}
              required
              className="w-full h-12 bg-[#12334a] rounded-xl border-none outline-none text-white px-4 text-sm font-[Alexandria] text-right"
              placeholder="أدخل اسم المحاضر"
            />
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2 text-right">
              الايميل
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full h-12 bg-[#12334a] rounded-xl border-none outline-none text-white px-4 text-sm font-[Alexandria] text-right"
              placeholder="أدخل الايميل الجامعي "
            />
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2 text-right">
              التخصص
            </label>
            <input
              type="text"
              name="specialization"
              value={formData.specialization}
              onChange={handleInputChange}
              required
              className="w-full h-12 bg-[#12334a] rounded-xl border-none outline-none text-white px-4 text-sm font-[Alexandria] text-right"
              placeholder="أدخل التخصص   "
            />
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2 text-right">
              اسم الكلية
            </label>
            <input
              type="text"
              name="collegeName"
              value={formData.collegeName}
              onChange={handleInputChange}
              required
              className="w-full h-12 bg-[#12334a] rounded-xl border-none outline-none text-white px-4 text-sm font-[Alexandria] text-right"
              placeholder="أدخل اسم الكلية"
            />
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2 text-right">
              بيانات التواصل
            </label>
            <input
              type="text"
              name="contactInfo"
              value={formData.contactInfo}
              onChange={handleInputChange}
              className="w-full h-12 bg-[#12334a] rounded-xl border-none outline-none text-white px-4 text-sm font-[Alexandria] text-right"
              placeholder="مثال: X: Hemh_uj"
            />
          </div>

          <div className="flex justify-center pt-4">
            <button
              type="submit"
              disabled={submitting}
              className={`w-32 h-10 border border-[#28e3da40] rounded-xl bg-transparent cursor-pointer flex items-center justify-center transition-colors ${
                submitting ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[#28e3da20]'
              }`}
            >
              <span className="font-[Alexandria] font-medium text-sm text-white">
                {submitting ? 'جاري الإرسال...' : 'إرسال الطلب'}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AddLecturerModal;
